/**
 * WorkspaceShell — Surface Layer (/01)
 *
 * The authenticated product shell: Carbon UIShell Header + SideNav rail +
 * content Outlet host. The SideNav renders EXACTLY ONE plane's navigation
 * (DevHub *or* UserHub) — the menus never mix, so a Workspace user can never
 * see a Developer Hub item and vice-versa. Identities that can enter both
 * planes switch with an explicit plane switcher (not a menu entry).
 *
 * Carbon-only, zero inline styles (Carbon tokens via colocated SCSS), default
 * theme g100.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Content,
  Dropdown,
  Header,
  HeaderContainer,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderName,
  SideNav,
  SideNavItems,
  SideNavLink,
  SkipToContent,
  Tag,
  Theme,
} from '@carbon/react';
import { Help, Logout, Notification, Settings, UserAvatar } from '@carbon/icons-react';
import { useSurfaceTheme } from '@theme/useSurfaceTheme';
import { useSurfaceSession } from '@auth/useSurfaceSession';
import { useAuthStore } from '@auth/auth-store';
import { useNotifications } from '@lib/persistence/useRuntimeData';
import { resolveRoleClass } from '../components/navigation/roleNavigation';
import { availablePlanes, PLANE_LABEL, type HubPlane } from '@lib/navigation/hubPlane';
import { navForPlane } from '@lib/navigation/hubNavigation';
import { useResolvedPlane, useActivePlaneStore } from '@lib/navigation/useActivePlane';
import { AppNotificationsPanel } from '../components/notifications/AppNotificationsPanel';
import { AppAboutModal } from '../components/about/AppAboutModal';
import { ProjectSwitcher } from './ProjectSwitcher';
import './workspace-shell.scss';

const EMPTY_ROLES: readonly string[] = [];

function isActivePath(current: string, to: string): boolean {
  if (to === '/dashboard') return current === '/dashboard';
  return current === to || current.startsWith(`${to}/`);
}

export function WorkspaceShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { merged } = useSurfaceTheme();
  const { email, signOut } = useSurfaceSession();
  const { unreadCount } = useNotifications('unread');

  const roles = useAuthStore((s) => s.identity?.roles) ?? EMPTY_ROLES;
  const roleClass = resolveRoleClass([...roles]);
  const planes = availablePlanes(roles);
  const activePlane = useResolvedPlane();
  const setActivePlane = useActivePlaneStore((s) => s.setActivePlane);
  const [notifOpen, setNotifOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  // Exactly one plane's nav, filtered by within-plane role capability.
  const navItems = navForPlane(activePlane).filter(
    (entry) => !entry.roleClasses || entry.roleClasses.includes(roleClass)
  );

  const contentTheme = merged.baseTheme === 'white' ? 'g10' : merged.baseTheme;
  const go = (to: string) => void navigate({ to });

  function switchPlane(plane: HubPlane) {
    setActivePlane(plane);
    go('/dashboard');
  }

  return (
    <Theme theme="g100">
      <div className="workspace-shell">
        <HeaderContainer
          render={({
            isSideNavExpanded,
            onClickSideNavExpand,
          }: {
            isSideNavExpanded: boolean;
            onClickSideNavExpand: () => void;
          }) => (
            <>
              <Header aria-label={`Cestari Studio ${PLANE_LABEL[activePlane]}`}>
                <SkipToContent />
                <HeaderMenuButton
                  aria-label={isSideNavExpanded ? 'Close menu' : 'Open menu'}
                  aria-expanded={isSideNavExpanded}
                  isActive={isSideNavExpanded}
                  isCollapsible
                  onClick={onClickSideNavExpand}
                />
                <HeaderName
                  href="/dashboard"
                  prefix="Cestari Studio"
                  onClick={(event: { preventDefault: () => void }) => {
                    event.preventDefault();
                    go('/dashboard');
                  }}
                >
                  {PLANE_LABEL[activePlane]}
                </HeaderName>
                <HeaderGlobalBar>
                  <HeaderGlobalAction
                    aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
                    isActive={notifOpen}
                    onClick={() => setNotifOpen((v) => !v)}
                  >
                    <Notification size={20} />
                  </HeaderGlobalAction>
                  <HeaderGlobalAction aria-label="Settings" onClick={() => go('/settings')}>
                    <Settings size={20} />
                  </HeaderGlobalAction>
                  <HeaderGlobalAction
                    aria-label="About Cestari Studio"
                    isActive={aboutOpen}
                    onClick={() => setAboutOpen(true)}
                  >
                    <Help size={20} />
                  </HeaderGlobalAction>
                  <HeaderGlobalAction aria-label="Account">
                    <UserAvatar size={20} />
                  </HeaderGlobalAction>
                  <HeaderGlobalAction aria-label="Sign out" onClick={() => void signOut()}>
                    <Logout size={20} />
                  </HeaderGlobalAction>
                </HeaderGlobalBar>
                <SideNav aria-label={`${PLANE_LABEL[activePlane]} navigation`} expanded={isSideNavExpanded} isFixedNav>
                  <SideNavItems>
                    {navItems.map((entry, i) => {
                      const baseLabel =
                        entry.to === '/notifications' && unreadCount > 0
                          ? `${entry.label} (${unreadCount})`
                          : entry.label;
                      const label = entry.comingSoon ? `${baseLabel} (soon)` : baseLabel;
                      return (
                        <SideNavLink
                          key={`${entry.to}-${i}`}
                          href={entry.comingSoon ? undefined : entry.to}
                          aria-disabled={entry.comingSoon || undefined}
                          isActive={!entry.comingSoon && isActivePath(pathname, entry.to)}
                          renderIcon={entry.icon}
                          onClick={(event: { preventDefault: () => void }) => {
                            event.preventDefault();
                            if (!entry.comingSoon) go(entry.to);
                          }}
                        >
                          {label}
                        </SideNavLink>
                      );
                    })}
                  </SideNavItems>
                </SideNav>
                <AppNotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
              </Header>
              <Theme as={Content} className="workspace-shell__content" theme={contentTheme}>
                <div className="workspace-shell__contextbar">
                  {planes.length > 1 ? (
                    <Dropdown
                      id="plane-switcher"
                      size="sm"
                      titleText=""
                      label="Active hub"
                      aria-label="Switch hub"
                      items={planes}
                      selectedItem={activePlane}
                      itemToString={(plane: HubPlane | null) => (plane ? PLANE_LABEL[plane] : '')}
                      onChange={({ selectedItem }: { selectedItem?: HubPlane | null }) => {
                        if (selectedItem) switchPlane(selectedItem);
                      }}
                    />
                  ) : null}
                  <ProjectSwitcher />
                  {email ? <Tag type="cool-gray">{email}</Tag> : null}
                </div>
                <div className="workspace-shell__view">{children}</div>
              </Theme>
            </>
          )}
        />
        <AppAboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} plane={activePlane} />
      </div>
    </Theme>
  );
}
